import React, { useEffect, useState, useContext, useCallback } from "react";

import Tag from "../productFilters/Tag";
import Button from "../button/Button";
import PausedMembers from "../popups/PausedMembers";

import { useShopContext } from "../../context/ShopContext";
import { useAppContext } from "../../context/AppContext";
import { ModalContext } from "../../context/ModalContext";
import { useUser } from "../../context/UserContext";

import Accordion from "../accordion/Accordion";

import styles from "./productInfo.module.css";

import {
  getSelectedProduct,
  // getColorOptions,
  // getSizeOptions,
  getProductVariants,
  checkPostcodeApproved,
  getTagsAsFilter,
  getSizesWithStock,
  createProductList,
  filterArrayForUniqueObjects,
} from "../../utils/productFunctions";

import Basket from "../basket/Basket";
import { Product } from "../../commonTypes";
import { useTracking } from "../../hooks/useTracking";
import BundleeMembers from "../popups/BundleeMembers";

const ProductInfo = ({ product }: { product: Product }) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [sizeOptions, setSizeOptions] = useState(null);
  const [colorOptions, setColorOptions] = useState(null);
  const { addItemToCart, cart } = useShopContext();
  const { postcodes, popupImage, pauseNewMembers } = useAppContext();
  const { handleModal } = useContext(ModalContext);
  const { userSubscribed } = useUser();
  const { trackAddToCart } = useTracking();

  // Gets product variants array,autoselects first color and size available
  const setProductOptionsWithDefaultSelections = useCallback(() => {
    //returns list of products mapped to each color e.g. [{beige: [{ product1 }, { product2 }], green: [{ product1 }, { product2 }]}]
    const productVariants = getProductVariants({ product });

    const colorMappedProducts = createProductList(productVariants);

    //returns array of color tag options
    const colorOptionsObjects = productVariants.map((item) => {
      return item.color;
    });

    //returns array of unique color tag options as tag objects
    const uniqueColorObjects = filterArrayForUniqueObjects(colorOptionsObjects);

    //sets coloroptions to be an array of unique color options
    !colorOptions && setColorOptions(uniqueColorObjects);

    //sets the first color option in the list as the selected (default) color
    !selectedColor && setSelectedColor(uniqueColorObjects[0]?.name);

    //sets the size options to be a list of the size option available to the chosen color
    const allProductsOfSelectedColor =
      colorMappedProducts[selectedColor || uniqueColorObjects[0]?.name];

    if (Array.isArray(allProductsOfSelectedColor)) {
      //maps these sizes + stock out into their own array
      const sizesWithStock = allProductsOfSelectedColor.map((item) => {
        return { size: item.size, stock: item.stock };
      });

      //sets size options to sizes + stock level
      setSizeOptions(sizesWithStock);

      //sets the first size option in the list as the selected (default) size
      !selectedSize && setSelectedSize(sizesWithStock[0].size.name);
    }
  }, [
    colorOptions,
    // sizeOptions,
    selectedColor,
    selectedSize,
    product,
    // variantOptions, // TODO - maybe use this going forward into tag?
    // selectedVariant,
  ]);

  useEffect(() => {
    setProductOptionsWithDefaultSelections();
  }, [setProductOptionsWithDefaultSelections, product]);

  const handleSelectColor = ({ name }) => {
    setSelectedColor(name);
    //remove for prod
  };

  const handleSelectSize = ({ name }) => {
    setSelectedSize(name);
    //remove for prod
  };

  const selectedProduct = getSelectedProduct({
    product: product,
    size: selectedSize,
    color: selectedColor,
  });

  //Selects correct product variant based on color/size selections
  const handleAddToBasket = () => {
    if (pauseNewMembers && !userSubscribed) {
      handleModal({
        content: <PausedMembers />,
        withImage: true,
      });
    } else if (selectedProduct) {
      addItemToCart(selectedProduct);
      trackAddToCart(selectedProduct);
      // Open basket
      // handleModal({ content: <Basket />, basket: true });
    }
  };

  //TODO - add reviews into this section
  //TODO - add recommended product grids to this section
  return (
    <div>
      <div className={styles.title}>{product.name}</div>
      <div className={styles.descriptionContainer}>
        <div className={styles.descriptionContainerTop}>
          <div className={styles.descriptionTitle}>{product.description}</div>
          <div className={styles.price}>RRP £{product.rrp}</div>
        </div>
        {/* <div className={styles.descriptionSubtext}> */}
        {/* {exampleDescriptionSubtext} */}
        {/* </div> */}
      </div>
      {colorOptions && colorOptions.length > 0 && (
        <div className={styles.color}>
          Color
          {/* Map through the available colors options and render tags for each */}
          <div className={styles.colorOptions}>
            {getTagsAsFilter(colorOptions).map((color, index) => {
              return (
                <Tag
                  key={index}
                  filter={color}
                  // color={color?.metadata?.hex || color.name} // Hex code for color -> styled in <Tag />
                  singleSelection={true}
                  onClick={handleSelectColor}
                  selected={selectedColor === color.name}
                />
              );
            })}
          </div>
        </div>
      )}
      {sizeOptions && sizeOptions.length > 0 && (
        <div className={styles.size}>
          Size
          <div className={styles.sizeOptions}>
            {getSizesWithStock(sizeOptions).map((size, index) => {
              return (
                <Tag
                  key={index}
                  filter={size}
                  singleSelection={true}
                  onClick={handleSelectSize}
                  selected={selectedSize === size.name}
                  stock={size.stock}
                />
              );
            })}
          </div>
        </div>
      )}
      <div className={styles.reviewsWithButton}>
        {/* <div>Review stars *****</div> */}
        <div className={styles.button}>
          <Button
            text="Add to basket"
            disabled={
              !selectedProduct.selected_variant ||
              product.product_variants.length < 1 ||
              selectedProduct?.selected_variant?.stock < 1
            }
            onClick={handleAddToBasket}
          />
          {/* <Button text="One time rental" />
          <Button text="Buy preloved" />p*/}
        </div>
      </div>
      {product.accordion1 && (
        <Accordion title={product.accordion1_title || "Product Details"}>
          {product.accordion1}
        </Accordion>
      )}

      <Accordion title={product.accordion2_title || "Care instructions"}>
        {product.accordion2 ||
          "We recommend 30 degree gentle wash to be kinder to the planet."}{" "}
      </Accordion>

      {product.accordion3 && (
        <Accordion title={product.accordion3_title || "Size + fit"}>
          {product.accordion3}
        </Accordion>
      )}
    </div>
  );
};

export default ProductInfo;
